/**
 * What floats inside the special bubbles (05 §6.4): a letter of EXTEND, a water drop, a
 * flame, a lightning bolt. Each is built centred on the origin, about 1.1 tiles tall, to sit
 * inside a bubble of radius 0.9. The letter is the HUD font's glyph extruded into voxels, so
 * the letters you collect and the letters on the panel are the same letters.
 */

import { Matrix4 } from 'three';
import { glyph, GLYPH } from '../../ui/font8';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';

/** The letter `ch` as voxels, `size` tiles tall. */
export function letterVoxels(p: Palette, ch: string, size = 1.05): MeshBuilder {
  const mb = new MeshBuilder();
  const px = size / GLYPH;
  const half = (GLYPH * px) / 2;
  const colours = { all: p.color('gold.1'), top: p.color('gold.2'), bottom: p.color('gold.0') };
  glyph(ch).forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      if (row[c] !== '#') continue;
      mb.box(px, px, px * 1.6, colours, place(c * px - half + px / 2, half - (r + 1) * px, 0));
    }
  });
  return mb;
}

/** A water drop: a round belly drawn up into a point. */
export function waterDrop(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(0.36, 0.36, 0.3, p.color('water.1'), place(0, -0.18, 0), { topColor: p.color('water.2'), detail: 2 });
  mb.cylinder(0.3, 0, 0.62, 8, p.color('water.1'), place(0, -0.02, 0), p.color('water.2'));
  mb.box(0.08, 0.16, 0.06, p.color('paper.0'), place(-0.14, -0.1, 0.28));
  return mb;
}

/** A flame: three tongues of the fire ramp, dark outside to white-hot inside. */
export function flame(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(0.38, 0.26, 0.3, p.color('fire.0'), place(0, -0.3, 0), { detail: 1 });
  mb.cylinder(0.36, 0, 0.95, 6, p.color('fire.1'), place(0, -0.45, 0), p.color('fire.0'));
  mb.cylinder(0.24, 0, 0.7, 6, p.color('fire.2'), place(0.02, -0.42, 0.1));
  mb.cylinder(0.12, 0, 0.42, 5, p.color('fire.3'), place(0.02, -0.4, 0.2));
  return mb;
}

/** A lightning bolt: two slanted bars and a point, in the bolt ramp. */
export function bolt(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  const c = { all: p.color('bolt.1'), top: p.color('bolt.2'), bottom: p.color('bolt.0') };
  const bar = (x: number, y: number, roll: number, len: number): void => {
    const m = new Matrix4().makeTranslation(x, y, 0).multiply(new Matrix4().makeRotationZ(roll));
    mb.box(0.2, len, 0.16, c, m, true);
  };
  bar(0.08, 0.26, -0.45, 0.62);
  bar(0, 0, 1.2, 0.46);
  bar(-0.08, -0.28, -0.45, 0.62);
  mb.cylinder(0.14, 0, 0.3, 4, p.color('bolt.2'), new Matrix4().makeTranslation(-0.2, -0.5, 0).multiply(
    new Matrix4().makeRotationZ(Math.PI - 0.45)));
  return mb;
}
